import { observer } from 'mobx-react';
import * as React from 'react';
import { Button, Grid } from 'ui-toolkit';
import { RequirementExceptionRow } from '../../components/sorting/RequirementException/RequirementExceptionRow';
import { SortableList } from '../../components/sorting/SortableList';
import { ICommitmentException } from '../classes/ICommitmentException';
import { ISpecificException } from '../classes/ISpecificExceptions';
import { ReqExcSections } from '../classes/ReqExcSections';
import { AddCustomReqExc } from './AddCustomReqExc/AddCustomReqExc';

export interface IRequirementsExceptions {
  addButtonText: string;
  addCustomItem: (sectionName: ReqExcSections, text: string) => void;
  addNewItem: (sectionName: ReqExcSections, item: ISpecificException) => void;
  className?: string;
  section: ReqExcSections;
  style?: React.CSSProperties;
  onDeleteItem: (sectionName: ReqExcSections, item: ICommitmentException) => void;
  onOrderChanged: (sectionName: ReqExcSections, newArray: ICommitmentException[]) => void;
  onTextChanged: (sectionName: ReqExcSections, newText: string, item: ICommitmentException) => void;
  requirementsExceptions: ICommitmentException[];
  specificExceptions: ISpecificException[];
  fetching: boolean;
}

interface IReqExcState {
  showAddCustom: boolean;
}

export const RequirementsExceptions = observer(
  class RequirementsExceptions extends React.Component<IRequirementsExceptions, IReqExcState> {
    public readonly state: IReqExcState = {
      showAddCustom: false,
    };

    public passDownProps = {
      style: this.props.style,
    };

    public render() {
      return (
        <Grid>
          {this.showRequirementsExceptions() ? (
            <SortableList
              items={this.props.requirementsExceptions || []}
              content={this.getItemContent}
              onDelete={this.onDeleteItem}
              onSort={this.onSort}
            />
          ) : null}
          {this.showEmptyText() ? <p>None at the moment. Any to add?</p> : null}
          {this.state.showAddCustom ? (
            <AddCustomReqExc
              section={this.props.section}
              options={this.props.specificExceptions}
              addCustomItem={this.onAddCustomItem}
              onDeleteClick={this.onAddClick}
              onCreateClick={this.onAddItem}
            />
          ) : (
            <Grid columnWidth={'min-content'}>
              <Button intent="secondary" onClick={this.onAddClick}>
                {this.props.addButtonText}
              </Button>
            </Grid>
          )}
        </Grid>
      );
    }

    private getItemContent = (item: ICommitmentException) => (
      <RequirementExceptionRow
        content={item.body}
        info={item.note}
        infoTitle={'More about this clause'}
        onTextChanged={(newText: string) => this.onTextChanged(newText, item)}
      />
    );

    private showRequirementsExceptions(): boolean {
      return (
        this.props.requirementsExceptions !== null &&
        this.props.requirementsExceptions !== undefined &&
        this.props.requirementsExceptions.length > 0
      );
    }

    private showEmptyText(): boolean {
      return (
        (this.props.requirementsExceptions === null ||
          this.props.requirementsExceptions === undefined ||
          this.props.requirementsExceptions.length === 0) &&
        !this.props.fetching
      );
    }

    private onAddCustomItem = (sectionName: ReqExcSections, text: string) => {
      this.onAddClick();
      this.props.addCustomItem(sectionName, text);
    };

    private onDeleteItem = (item: ICommitmentException) => {
      this.props.onDeleteItem(this.props.section, item);
    };

    private onAddItem = (item: ISpecificException) => {
      this.onAddClick();
      this.props.addNewItem(this.props.section, item);
    };

    private onTextChanged = (newText: string, item: ICommitmentException) => {
      this.props.onTextChanged(this.props.section, newText, item);
    };

    private onAddClick = () => {
      this.setState((state) => ({
        ...state,
        showAddCustom: !state.showAddCustom,
      }));
    };

    private onSort = (items: ICommitmentException[]) => {
      this.props.onOrderChanged(this.props.section, items);
    };
  }
);
